import React from "react";
import "./AgencyCard.css";
import Image from '../Image/Image';
import pantryIcon from '../../assets/icons/pantry.png';
import soupKitchenIcon from '../../assets/icons/soup-kitchen.png';
import babyItemPantryIcon from '../../assets/icons/baby-item-pantry.png';
// Add more imports as needed

const getIcon = (agency: any): string => {
  if (agency.programs && agency.programs.length > 0) {
    // Priority order: Soup Kitchen > Baby Item Pantry > Pantry
    if (agency.programs.includes("Soup Kitchen")) return soupKitchenIcon;
    if (agency.programs.includes("Baby Item Pantry")) return babyItemPantryIcon;
    if (agency.programs.includes("Pantry")) return pantryIcon;
    // Add more program types as needed
  }
  return pantryIcon; // Default to pantry icon
};

type Agency = {
  id: string;
  name: string;
  distance: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  phone: string;
  directionsUrl: string;
  hours: string;
  programs: string[]; // e.g., ['pantry', 'baby-item-pantry']
};

type Props = {
  agency: Agency;
  onViewOnMap?: (agency: Agency) => void;
};

const AgencyCard: React.FC<Props> = ({ agency, onViewOnMap }) => {
  const handlePrint = () => {
    // Create a printable version of the agency information
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const printContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Agency Information - ${agency.name}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              h1 { color: #333; border-bottom: 2px solid #333; }
              .section { margin: 15px 0; }
              .label { font-weight: bold; color: #666; }
              .programs { margin-top: 10px; }
            </style>
          </head>
          <body>
            <h1>${agency.name}</h1>
            <div class="section">
              <span class="label">Distance:</span> ${agency.distance} miles
            </div>
            <div class="section">
              <span class="label">Address:</span><br>
              ${agency.address}<br>
              ${agency.city}, ${agency.state} ${agency.zip}, ${agency.country}
            </div>
            <div class="section">
              <span class="label">Phone:</span> ${agency.phone || 'Not available'}
            </div>
            <div class="section">
              <span class="label">Hours:</span><br>
              ${agency.hours || 'Not specified'}
            </div>
            <div class="section programs">
              <span class="label">Programs:</span><br>
              ${agency.programs?.map(program => program.replace(/-/g, ' ')).join(', ') || 'Not specified'}
            </div>
            <div class="section">
              <span class="label">Directions:</span><br>
              <a href="${agency.directionsUrl}" target="_blank">Get Directions</a>
            </div>
          </body>
        </html>
      `;
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  };

  return (
    <li className="agency-card" data-id={agency.id}>
      <div className="agency-card__header">
        <button
          type="button"
          className="agency-card__title-button"
          aria-label={`View ${agency.name} on map`}
          onClick={() => onViewOnMap?.(agency)}
        >
          <h4 className="agency-card__name">
            {agency.name}
          </h4>
        </button>
        <div className="agency-card__header-right">
          <span className="agency-card__distance">
            — {agency.distance} miles
          </span>
          <button
            type="button"
            className="agency-card__print-button agency-card__print-button--header"
            onClick={handlePrint}
            aria-label={`Print ${agency.name} information`}
            title="Print agency information"
          >
            🖨️ Print
          </button>
        </div>
      </div>

      <div className="agency-card__content">
        <div className="agency-card__section agency-card__location">
          <h5 className="agency-card__label">Address:</h5>
          <p className="agency-card__text">
            {agency.address}
            <br />
            {agency.city}, {agency.state} {agency.zip}, {agency.country}
          </p>
        </div>

        <div className="agency-card__section agency-card__directions">
          <a
            href={agency.directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="agency-card__directions-link"
          >
            Get Directions
          </a>
        </div>

        <div className="agency-card__section agency-card__phone">
          <h5 className="agency-card__label">Phone:</h5>
          <p className="agency-card__text">{agency.phone}</p>
        </div>

        <div className="agency-card__section agency-card__hours">
          <h5 className="agency-card__label">Hours:</h5>
          <p className="agency-card__text">{agency.hours}</p>
        </div>

        <div className="agency-card__section agency-card__programs">
          <h5 className="agency-card__label">Programs:</h5>
          <div className="agency-card__program-list">
            <div className="agency-card__program-item">
              <Image
                src={getIcon(agency)}
                alt={agency.programs.join(", ")}
                width={19}
                height={19}
                className="agency-card__program-icon"
              />
              {agency.programs.map((program, index) => (
                <span key={index}>{program.replace(/-/g, " ")}{index < agency.programs.length - 1 ? ', ' : ''}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};

export default AgencyCard;
